import axios from "axios";
import eventEmitter from "./eventEmitter";

const API_URL = import.meta.env.VITE_API_URL;

// Token yönetimi için singleton
const TokenManager = {
  token: null,
  lastTokenTime: null,
  isInitializing: false,
  pendingRequests: [],

  async initialize() {
    if (this.isInitializing) {
      return new Promise((resolve, reject) => {
        this.pendingRequests.push({ resolve, reject });
      });
    }

    this.isInitializing = true;

    try {
      const response = await axios.get(`${API_URL}/api/auth/csrf-token`, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      this.token = response.data.csrfToken;
      this.lastTokenTime = Date.now();
      this.isInitializing = false;

      // Bekleyen istekleri çözümle
      this.pendingRequests.forEach(({ resolve }) => resolve(this.token));
      this.pendingRequests = [];

      return this.token;
    } catch (error) {
      this.isInitializing = false;
      this.pendingRequests.forEach(({ reject }) => reject(error));
      this.pendingRequests = [];
      throw error;
    }
  },

  async getToken() {
    // Token yoksa veya süresi geçmişse (14 dakika)
    if (
      !this.token ||
      !this.lastTokenTime ||
      Date.now() - this.lastTokenTime > 14 * 60 * 1000
    ) {
      return this.initialize();
    }
    return this.token;
  },
};

export const api = axios.create({
  baseURL: API_URL + "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*",
  },
  withCredentials: true,
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await TokenManager.getToken();
      if (token) {
        config.headers["X-CSRF-Token"] = token;
      }
      return config;
    } catch (error) {
      console.error("Token hatası:", error);
      return config; // Token hatası olsa bile isteği gönder
    }
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      // Network hatası veya timeout
      console.error("Network hatası:", error);
      return Promise.reject({
        message:
          "Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.",
      });
    }

    const originalRequest = error.config;

    // CSRF hatası kontrolü
    if (
      error.response?.status === 403 &&
      (error.response?.data?.message?.includes("CSRF") ||
        error.response?.data?.csrf === false) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        TokenManager.token = null;
        TokenManager.lastTokenTime = null;
        const token = await TokenManager.getToken();
        originalRequest.headers["X-CSRF-Token"] = token;
        return api(originalRequest);
      } catch (retryError) {
        console.error("CSRF yenileme hatası:", retryError);
        return Promise.reject(retryError);
      }
    }

    // Hata mesajını düzenle
    const errorMessage =
      error.response?.data?.message || error.message || "Bir hata oluştu";
    error.message = errorMessage;
    return Promise.reject(error);
  }
);

export default api;
